import {HTMLElement, append, removeChildNodes} from "@acng/frontend-bounty";

import {REGISTRY_ELEMENT} from "../model/resource.js";
import {element, nodes, render, scope} from "../service/render.js";
import {connectedCallback, disconnectedCallback} from "@acng/frontend-bounty/dom/custom.js";

/**
 * @template {import("../service/render").Nodes} [N = import("../service/render").Nodes]
 * @implements {WidgetInterface}
 */
export class Widget extends HTMLElement {
  /**
   * @internal
   * @type {HTMLTemplateElement | undefined}
   */
  static [REGISTRY_ELEMENT];

  /**
   * @type {boolean | string[] | string}
   */
  static template = true;

  constructor() {
    super();

    const template = /** @type {typeof Widget} */ (this.constructor)[REGISTRY_ELEMENT];

    /**
     * @internal
     * @type {?import("../service/render").RenderingContext<Widget<N>, Widget<N>, N>}
     */
    this.rendering = template
      ? {
          [REGISTRY_ELEMENT]: template,
          [scope]: this,
          [element]: this,
          [nodes]: /** @type {N} */ ({}),
        }
      : null;
  }

  /**
   * @internal
   */
  [connectedCallback]() {
    this.applyTemplate();
  }

  /**
   * @internal
   */
  [disconnectedCallback]() {
    if (this.rendering) {
      removeChildNodes(this);
      this.rendering[nodes] = /** @type {N} */ ({});
    }
  }

  /**
   * @returns {N | undefined}
   */
  get nodes() {
    return this.rendering?.[nodes];
  }

  /**
   * Never call this from render or link!
   */
  applyTemplate() {
    if (!this.rendering) {
      return;
    }

    removeChildNodes(this);

    const fragment = render(this.rendering);
    this.render();
    append(this, fragment);
    this.link();
  }

  render() {}
  link() {}
}

/**
 * @template {HTMLElement & Record<string, any>} T
 * @typedef {{
 *  new(): T;
 *  prototype: T;
 *  setup?: () => Promise<void>;
 *  [REGISTRY_ELEMENT]?: HTMLTemplateElement;
 *  consumables?: readonly import("@acng/frontend-relativity").Context<unknown>[];
 *  template?: boolean | string[] | string;
 * }} WidgetConstructor
 */

/**
 * @typedef {import("../service/render").Scope} WidgetInterface
 */
